import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lightbulb, Building2, Crosshair, BookOpen, ShieldCheck,
  ChevronLeft, ChevronRight, Loader2, ArrowLeft, ExternalLink,
  Calculator, Ruler, FileText, Wrench, Moon, Sun, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DesignIntelligence from '../components/design-companion/DesignIntelligence';
import QuickCapturePanel from '../components/design-companion/QuickCapturePanel';
import StructuralGuide from '../components/design-companion/StructuralGuide';
import CodeCompliance from '../components/design-companion/CodeCompliance';
import ChiefArchitectGuide from '../components/design-companion/ChiefArchitectGuide';
import OpeningCountTool from '../components/design-companion/OpeningCountTool';
import MaterialEstimator from '../components/design-companion/MaterialEstimator';
import { getCurrentUserId } from '../utils/currentUser';
import ReferenceToolsBar from '@/components/ReferenceToolsBar';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'intelligence', label: 'Design Intelligence', icon: Lightbulb, color: '#F59E0B', ready: true },
  { id: 'structural', label: 'Structural Guide', icon: Building2, color: '#3B82F6', ready: true },
  { id: 'capture', label: 'Live Capture', icon: Crosshair, color: '#10B981', ready: true },
  { id: 'library', label: 'Resource Library', icon: BookOpen, color: '#8B5CF6', ready: true },
  { id: 'compliance', label: 'Code & Compliance', icon: ShieldCheck, color: '#EC4899', ready: true },
  { id: 'materials', label: 'Material Estimator', icon: Package, color: '#0D9488', ready: true },
];

// Pre-engineering tools that link from Structural Guide.
// `tool` IDs MUST match PreEngineeringTools.js TOOL_CATEGORIES keys.
const STRUCTURAL_LINKS = [
  { label: 'Header Sizing Reference', desc: 'Span tables for headers by opening width & load', tool: 'header_sizing' },
  { label: 'Rafter Calculator', desc: 'Rafter span by species, grade, spacing', tool: 'rafter_calc' },
  { label: 'Deck Load Calculator', desc: 'Joist/beam sizing for decks', tool: 'deck_load' },
  { label: 'Point Load Identifier', desc: 'Identify and size point loads', tool: 'point_load' },
];

// Pre-engineering tools that link from Code & Compliance.
// `tool` IDs MUST match the activeTool keys in PreEngineeringTools.js
// (TOOL_CATEGORIES). Pre-fix the IDs were misnamed (egress/r_value/glazing)
// and the path was a dead `/pre-engineering` route — fixed 2026-06-19.
const COMPLIANCE_LINKS = [
  { label: 'Egress Calculator', desc: 'Emergency egress window sizing', tool: 'egress_calc' },
  { label: 'Stair Calculator', desc: 'Rise/run/headroom code compliance', tool: 'stair_calc' },
  { label: 'Smoke & CO Layout', desc: 'Detector placement per IRC', tool: 'smoke_co' },
  { label: 'FAR Calculator', desc: 'Floor area ratio & setback check', tool: 'far_calc' },
  { label: 'R-Value Calculator', desc: 'Insulation R-value by assembly', tool: 'rvalue_calc' },
  { label: 'Glazing Ratio Analyzer', desc: 'Window-to-wall ratio check', tool: 'glazing_ratio' },
];

// Resource Library categories
const LIBRARY_CATEGORIES = [
  { label: 'Building Codes', desc: 'IRC 2018 quick reference, IECC by climate zone', icon: ShieldCheck },
  { label: 'Span & Structural Tables', desc: 'Floor joist, rafter, header, deck spans', icon: Building2 },
  { label: 'Design Standards', desc: 'Kitchen, bath, stair, lighting rules of thumb', icon: Ruler },
  { label: 'Material Reference', desc: 'Lumber grades, roofing, siding, windows, insulation', icon: Wrench },
  { label: 'Design Software Tips', desc: 'Shortcuts, macros, schedule tips for your software', icon: FileText },
  { label: 'Vendor & Product Links', desc: 'Saved vendor contacts and product links', icon: ExternalLink },
];

export default function DesignCompanionPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // "Back" — return to where the user came from, NOT app root. Mirrors
  // the ClipDetail.handleBack pattern that fixed the "Back to Library"
  // bug class. Without this, deep-linking into Design Companion (or
  // launching it via a PWA shortcut) leaves no real history → the bare
  // navigate(-1) falls through to whatever the tab last saw (usually
  // /dashboard), giving the "takes me all the way back to My Desk"
  // symptom even when the user came from a project hub.
  //
  // Priority:
  //   1) Real browser history (location.key !== 'default') — restores
  //      scroll position and any in-memory filters naturally.
  //   2) Explicit `state.from` carried over from the source link.
  //   3) Project hub (when a projectId is in the URL).
  //   4) Last resort: app root.
  const handleBack = () => {
    const hasHistory = location.key && location.key !== 'default';
    if (hasHistory) { navigate(-1); return; }
    const fromState = location.state?.from;
    if (fromState) { navigate(fromState); return; }
    if (projectId) { navigate(`/projects/${projectId}/hub`); return; }
    navigate('/');
  };
  const [activeSection, setActiveSection] = useState('intelligence');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('keyMadeDarkMode') === 'true'; } catch { return false; }
  });
  const [project, setProject] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickCapture, setShowQuickCapture] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  // TK-12f — the Takeoff card opens the project in the URL, or the one picked on this page.
  const takeoffProjectId = projectId || (selectedProjectId && selectedProjectId !== 'none' ? selectedProjectId : '');
  const [siteContext, setSiteContext] = useState(null);
  const [showOpeningCount, setShowOpeningCount] = useState(false);

  // Load project data
  const loadProject = useCallback(async (pid) => {
    if (!pid || pid === 'none') { setProject(null); setRooms([]); setSiteContext(null); setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/api/projects/${pid}?user_id=${getCurrentUserId()}`);
      setProject(res.data);
      // Load rooms
      try {
        const roomRes = await axios.get(`${API}/api/projects/${pid}/rooms?user_id=${getCurrentUserId()}`);
        setRooms(roomRes.data.rooms || []);
      } catch { setRooms([]); }
      // Load site context for Design Companion tools
      try {
        const ctxRes = await axios.get(`${API}/api/design-companion/site-context/${pid}`);
        setSiteContext(ctxRes.data?.context || null);
      } catch { setSiteContext(null); }
    } catch (err) {
      setProject(null);
      setRooms([]);
      setSiteContext(null);
    }
    setLoading(false);
  }, []);

  // Load projects list
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await axios.get(`${API}/api/projects?user_id=${getCurrentUserId()}`);
        // API may return array directly or wrapped in {projects: [...]}
        const projectsList = Array.isArray(res.data) ? res.data : (res.data.projects || []);
        setProjects(projectsList);
      } catch {}
    };
    loadProjects();
  }, []);

  useEffect(() => {
    loadProject(selectedProjectId);
  }, [selectedProjectId, loadProject]);

  const toggleDarkMode = () => {
    const newVal = !darkMode;
    setDarkMode(newVal);
    try { localStorage.setItem('keyMadeDarkMode', String(newVal)); } catch {}
  };

  // Keyboard shortcut: Cmd+D for Quick Capture
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        setShowQuickCapture(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F8F7F5]';
  const sidebarBg = darkMode ? 'bg-[#141416]' : 'bg-white';
  const border = darkMode ? 'border-[#2A2A2A]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-[#1A1A1E]' : 'bg-white';

  return (
    <div className={`h-screen flex flex-col ${bg}`} data-testid="design-companion-page">
      {/* Top Bar */}
      <div className={`h-12 flex items-center justify-between px-4 border-b ${border} ${sidebarBg} flex-shrink-0`}>
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className={`p-1 rounded hover:bg-gray-100 ${darkMode ? 'hover:bg-[#2A2A2A]' : ''}`}>
            <ArrowLeft className={`w-4 h-4 ${textMuted}`} />
          </button>
          <h1 className={`text-sm font-bold tracking-wide ${text}`}>DESIGN COMPANION</h1>
          <span className="w-px h-4 bg-gray-300" />

          {/* Project selector */}
          <Select value={selectedProjectId} onValueChange={(v) => { setSelectedProjectId(v); if (!projectId) navigate(`/design-companion`); }}>
            <SelectTrigger className={`h-7 text-xs w-52 ${darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-200' : ''}`} data-testid="project-selector">
              <SelectValue placeholder="No project selected" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No project (reference mode)</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.client_name || p.name || p.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {project && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#BC5D41]/10 text-[#BC5D41] font-medium">
              {project.status || 'Active'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowQuickCapture(!showQuickCapture)}
            className={`h-7 text-xs gap-1.5 ${darkMode ? 'border-[#444] text-gray-300 hover:bg-[#2A2A2E]' : ''}`}
            data-testid="quick-capture-toggle">
            <Crosshair className="w-3 h-3" />
            Capture
            <kbd className={`ml-1 text-[9px] px-1 py-0.5 rounded ${darkMode ? 'bg-[#333]' : 'bg-gray-100'}`}>
              {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}D
            </kbd>
          </Button>
          <button onClick={toggleDarkMode} className={`p-1.5 rounded ${textMuted} hover:text-[#BC5D41]`}>
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className={`${sidebarBg} border-r ${border} flex flex-col ${sidebarCollapsed ? 'w-14' : 'w-56'} transition-all duration-200 flex-shrink-0`}>
          <nav className="flex-1 py-2">
            {SECTIONS.map(sec => (
              <button
                key={sec.id}
                onClick={() => setActiveSection(sec.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors ${activeSection === sec.id
                  ? `${darkMode ? 'bg-[#BC5D41]/10' : 'bg-[#BC5D41]/5'} text-[#BC5D41]`
                  : `${textMuted} hover:${darkMode ? 'bg-[#1A1A1E]' : 'bg-gray-50'}`
                }`}
                data-testid={`dc-nav-${sec.id}`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: activeSection === sec.id ? `${sec.color}15` : 'transparent' }}>
                  <sec.icon className="w-4 h-4" style={{ color: activeSection === sec.id ? sec.color : undefined }} />
                </div>
                {!sidebarCollapsed && (
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{sec.label}</div>
                    {!sec.ready && <span className="text-[9px] opacity-50">Preview</span>}
                  </div>
                )}
              </button>
            ))}
          </nav>

          {/* DP21 — the firm's resource library, its own panel in the assistant.
              Hidden when the rail is collapsed, so it never squeezes the nav. */}
          {!sidebarCollapsed && (
            <div className="px-2 pb-2">
              <ReferenceToolsBar darkMode={darkMode} compact title="Resources" />
            </div>
          )}

          {/* Collapse toggle */}
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`p-3 border-t ${border} ${textMuted} hover:text-[#BC5D41] flex justify-center`}>
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-[#BC5D41]" />
            </div>
          ) : (
            <>
              {activeSection === 'intelligence' && (
                <DesignIntelligence
                  projectId={selectedProjectId !== 'none' ? selectedProjectId : null}
                  rooms={rooms}
                  darkMode={darkMode}
                />
              )}

              {activeSection === 'structural' && (
                <div className="overflow-auto h-full">
                  <StructuralGuide
                    darkMode={darkMode}
                    onNavigatePreEng={(toolId) => {
                      // Pre-Engineering lives as a ProjectHubFull tab, not a
                      // standalone route. Need an active project to land there.
                      if (selectedProjectId && selectedProjectId !== 'none') {
                        const qs = toolId ? `&tool=${toolId}` : '';
                        navigate(`/projects/${selectedProjectId}/hub?tab=preengineering${qs}`);
                      } else {
                        toast.error('Pick a project first — Pre-Engineering is per-project.');
                      }
                    }}
                    siteContext={siteContext}
                  />
                </div>
              )}

              {activeSection === 'capture' && (
                <div className="overflow-auto h-full">
                  {showOpeningCount ? (
                    <OpeningCountTool
                      darkMode={darkMode}
                      projectId={selectedProjectId !== 'none' ? selectedProjectId : null}
                      onBack={() => setShowOpeningCount(false)}
                    />
                  ) : (
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className={`text-lg font-semibold mb-1 ${text}`}>Live Capture</h2>
                    <p className={`text-sm ${textMuted}`}>Capture dimensions, counts, and notes during active design work. All data saves to your project record.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <button onClick={() => setShowQuickCapture(true)}
                      className={`${cardBg} border ${border} rounded-xl p-5 text-left hover:border-[#BC5D41]/50 transition-all`}
                      data-testid="open-quick-capture">
                      <Crosshair className="w-5 h-5 text-[#10B981] mb-2" />
                      <div className={`font-medium text-sm ${text}`}>Quick Capture Panel</div>
                      <div className={`text-xs ${textMuted} mt-1`}>Floating widget for dimensions, counts, volumes, and notes</div>
                    </button>
                    <button onClick={() => takeoffProjectId && navigate(`/projects/${takeoffProjectId}/floor-plans`)}
                      disabled={!takeoffProjectId} title={takeoffProjectId ? undefined : 'Pick a project first'}
                      data-testid="design-companion-takeoff"
                      className={`${cardBg} border ${border} rounded-xl p-5 text-left enabled:hover:border-[#BC5D41]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}>
                      <Ruler className="w-5 h-5 text-[#3B82F6] mb-2" />
                      <div className={`font-medium text-sm ${text}`}>Takeoff</div>
                      <div className={`text-xs ${textMuted} mt-1`}>Detailed room-by-room measurement capture</div>
                    </button>
                    <button onClick={() => navigate('/pdf-measure')}
                      className={`${cardBg} border ${border} rounded-xl p-5 text-left hover:border-[#BC5D41]/50 transition-all`}>
                      <FileText className="w-5 h-5 text-[#8B5CF6] mb-2" />
                      <div className={`font-medium text-sm ${text}`}>PDF Measure Tool</div>
                      <div className={`text-xs ${textMuted} mt-1`}>Measure directly on PDF plan documents</div>
                    </button>
                    <button onClick={() => setShowOpeningCount(true)}
                      className={`${cardBg} border ${border} rounded-xl p-5 text-left hover:border-[#BC5D41]/50 transition-all`}
                      data-testid="open-opening-count">
                      <Calculator className="w-5 h-5 text-[#F59E0B] mb-2" />
                      <div className={`font-medium text-sm ${text}`}>Opening Count Tool</div>
                      <div className={`text-xs ${textMuted} mt-1`}>Track doors, windows, and openings by room</div>
                    </button>
                  </div>

                  {/* Design Software workflow */}
                  <div className={`${cardBg} border ${border} rounded-xl p-5`}>
                    <h3 className={`font-medium text-sm mb-2 ${text}`}>Design Software Workflow</h3>
                    <p className={`text-xs ${textMuted} mb-3`}>
                      Speed up data capture from your design software. Use the Quick Capture panel alongside your software to record measurements as you work.
                    </p>
                    <div className={`text-xs ${textMuted} space-y-2`}>
                      <div className="flex gap-2"><span className="text-[#BC5D41] font-bold">1.</span> Open Quick Capture (Cmd+D)</div>
                      <div className="flex gap-2"><span className="text-[#BC5D41] font-bold">2.</span> Copy dimensions from your design software</div>
                      <div className="flex gap-2"><span className="text-[#BC5D41] font-bold">3.</span> Paste into the dimension fields and assign to a room</div>
                      <div className="flex gap-2"><span className="text-[#BC5D41] font-bold">4.</span> All captures auto-save to your project record</div>
                    </div>
                  </div>
                </div>
                  )}
                </div>
              )}

              {activeSection === 'library' && (
                <div className="overflow-auto h-full">
                  <div className={`p-6 space-y-6 ${bg}`}>
                    <div>
                      <h2 className={`text-lg font-semibold mb-1 ${text}`}>Resource Library</h2>
                      <p className={`text-sm ${textMuted}`}>Curated knowledge base for residential design & construction.</p>
                    </div>

                    {/* Chief Architect Featured Guide */}
                    <button onClick={() => setActiveSection('chief-architect')}
                      className={`w-full ${cardBg} border-2 border-[#BC5D41]/30 rounded-xl p-5 text-left hover:border-[#BC5D41]/60 transition-colors group`}
                      data-testid="chief-architect-guide-link">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#BC5D41]/10 flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-6 h-6 text-[#BC5D41]" />
                        </div>
                        <div className="flex-1">
                          <div className={`font-semibold text-sm ${text} group-hover:text-[#BC5D41]`}>Chief Architect Workflow Guide</div>
                          <div className={`text-xs ${textMuted} mt-1`}>Project setup, export settings, KEY-MADE integration, and shortcuts for Chief Architect users</div>
                        </div>
                        <ChevronRight className={`w-5 h-5 ${textMuted} group-hover:text-[#BC5D41]`} />
                      </div>
                    </button>

                    {/* Category Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {LIBRARY_CATEGORIES.map(cat => (
                        <button key={cat.label} onClick={() => navigate('/knowledge')}
                          className={`${cardBg} border ${border} rounded-xl p-5 text-left hover:border-[#BC5D41]/40 transition-colors group`}>
                          <cat.icon className={`w-5 h-5 mb-2 ${textMuted} group-hover:text-[#BC5D41]`} />
                          <div className={`font-medium text-sm ${text}`}>{cat.label}</div>
                          <div className={`text-xs ${textMuted} mt-1`}>{cat.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'chief-architect' && (
                <div className="overflow-auto h-full">
                  <div className={`px-6 pt-4 pb-0 ${bg}`}>
                    <button onClick={() => setActiveSection('library')}
                      className={`flex items-center gap-1 text-xs ${textMuted} hover:text-[#BC5D41] mb-2`}>
                      <ArrowLeft className="w-3 h-3" /> Back to Resource Library
                    </button>
                  </div>
                  <ChiefArchitectGuide darkMode={darkMode} />
                </div>
              )}

              {activeSection === 'compliance' && (
                <div className="overflow-auto h-full">
                  <CodeCompliance darkMode={darkMode} projectId={selectedProjectId !== 'none' ? selectedProjectId : null} siteContext={siteContext} />
                  {/* Links to Pre-Engineering Code Calculators */}
                  <div className={`px-6 pb-6 space-y-4 ${bg}`}>
                    <h3 className={`text-sm font-medium ${textMuted}`}>Code Calculators</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {COMPLIANCE_LINKS.map(link => {
                        const hasProject = selectedProjectId && selectedProjectId !== 'none';
                        return (
                          <button
                            key={link.label}
                            disabled={!hasProject}
                            title={hasProject ? `Open ${link.label} in Pre-Engineering` : 'Pick a project first — Pre-Engineering is per-project.'}
                            onClick={() => navigate(`/projects/${selectedProjectId}/hub?tab=preengineering&tool=${link.tool}`)}
                            className={`${cardBg} border ${border} rounded-lg p-4 text-left hover:border-[#BC5D41]/40 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className={`text-xs font-medium ${text} group-hover:text-[#BC5D41]`}>{link.label}</div>
                                <div className={`text-[11px] ${textMuted} mt-0.5`}>{link.desc}</div>
                              </div>
                              <ExternalLink className={`w-3.5 h-3.5 ${textMuted} opacity-0 group-hover:opacity-100`} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {selectedProjectId && selectedProjectId !== 'none' && (
                      <button onClick={() => navigate(`/projects/${selectedProjectId}/quality-control`)}
                        className={`w-full ${cardBg} border ${border} rounded-xl p-5 text-left hover:border-[#BC5D41]/40 transition-colors group`}>
                        <div className={`font-medium text-sm ${text} group-hover:text-[#BC5D41]`}>Quality Control Checklists</div>
                        <div className={`text-xs ${textMuted} mt-1`}>Room-by-room, trade-by-trade inspection checklists for this project</div>
                      </button>
                    )}
                  </div>
                </div>
              )}
              {activeSection === 'materials' && (
                <div className="overflow-auto h-full">
                  <MaterialEstimator darkMode={darkMode} projectId={selectedProjectId !== 'none' ? selectedProjectId : null} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Quick Capture floating panel */}
      {showQuickCapture && (
        <QuickCapturePanel
          projectId={selectedProjectId !== 'none' ? selectedProjectId : null}
          rooms={rooms}
          darkMode={darkMode}
          onClose={() => setShowQuickCapture(false)}
        />
      )}
    </div>
  );
}
